import React, { useState } from "react";
import { makeStyles } from "@seahorse/ui";
import { formatExecutionTimestampWithZone } from "../../Utils/timezone";
import { useDisplayTimeZone } from "../../Utils/displayTimeZone";

export type BestXExampleTrade = {
  currencyPair: string;
  executionTimestamp: string;
  executionSpotRate: number;
  executionForwardPoints: number;
  expirationTimestamp?: string;
  /** Rates shown after the pair and time, e.g. "Spot : 160, Fwd Pt: 0". */
  rates: string;
  event: string;
};

const EXAMPLE_TRADES: BestXExampleTrade[] = [
  {
    currencyPair: "USDJPY",
    executionTimestamp: "2026-07-30 14:15:00",
    executionSpotRate: 160,
    executionForwardPoints: 0,
    rates: "Spot : 160, Fwd Pt: 0",
    event: "Japan FX intervention",
  },
  {
    currencyPair: "EURUSD",
    executionTimestamp: "2026-08-07 12:35:00",
    executionSpotRate: 1.16,
    executionForwardPoints: 0,
    rates: "Spot : 1.16, Fwd Pt: 0",
    event: "U.S. payroll downside surprise",
  },
  {
    currencyPair: "NZDUSD",
    executionTimestamp: "2026-09-02 02:05:00",
    executionSpotRate: 0.6,
    executionForwardPoints: 0,
    rates: "Spot : 0.60, Fwd Pt: 0",
    event: "RBNZ rate decision",
  },
  {
    currencyPair: "USDCAD",
    executionTimestamp: "2026-09-04 12:35:00",
    executionSpotRate: 1.38,
    executionForwardPoints: 0,
    rates: "Spot : 1.38, Fwd Pt: 0",
    event: "Diverging U.S. and Canadian employment data",
  },
];

interface BestXExampleTradeLoaderProps {
  onSelect: (trade: BestXExampleTrade) => void;
  placement?: "floating" | "inline";
}

/**
 * Removable example-data affordance. It only populates the landing form;
 * analysis remains a deliberate action in the parent component.
 */
const BestXExampleTradeLoader: React.FC<BestXExampleTradeLoaderProps> = ({
  onSelect,
  placement = "floating",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { timeZone } = useDisplayTimeZone();
  const useStyles = makeStyles({
    container:
      placement === "inline"
        ? {
            position: "relative",
            display: "inline-flex",
            zIndex: 2,
          }
        : {
            position: "absolute",
            top: "24px",
            right: "24px",
            zIndex: 2,
          },
    trigger: {
      cursor: "pointer",
      padding: "7px 10px",
      borderRadius: "999px",
      border: "1px solid rgba(157, 204, 255, 0.65)",
      background: "transparent",
      color: "#D6EAF8",
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: "16px",
      whiteSpace: "nowrap",
      "&:hover": {
        background: "rgba(93, 173, 226, 0.12)",
      },
      "&:focus-visible": {
        outline: "2px solid #4C9AFF",
        outlineOffset: "2px",
      },
    },
    menu: {
      position: "absolute",
      top: "calc(100% + 8px)",
      right: 0,
      width: "340px",
      maxWidth: "calc(100vw - 48px)",
      boxSizing: "border-box",
      padding: "6px",
      borderRadius: "10px",
      border: "1px solid rgba(255,255,255,0.12)",
      background: "#26303D",
      boxShadow: "0 12px 28px rgba(0, 0, 0, 0.3)",
    },
    menuItem: {
      width: "100%",
      cursor: "pointer",
      border: 0,
      borderRadius: "7px",
      padding: "9px 10px",
      background: "transparent",
      color: "#E0E0E0",
      textAlign: "left",
      "&:hover": {
        background: "rgba(255,255,255,0.08)",
      },
      "&:focus-visible": {
        outline: "2px solid #4C9AFF",
        outlineOffset: "-2px",
      },
    },
    tradeLabel: {
      display: "block",
      fontSize: "12px",
      fontWeight: 700,
      lineHeight: "17px",
    },
    event: {
      display: "block",
      marginTop: "2px",
      color: "rgba(224,224,224,0.66)",
      fontSize: "12px",
      lineHeight: "16px",
    },
  });
  const classes = useStyles();

  return (
    <div className={classes.container}>
      <button
        type="button"
        className={classes.trigger}
        onClick={() => setIsOpen((open) => !open)}
        aria-expanded={isOpen}
        aria-haspopup="menu"
      >
        Load example trade ▾
      </button>
      {isOpen ? (
        <div className={classes.menu} role="menu" aria-label="Example trades">
          {EXAMPLE_TRADES.map((trade) => (
            <button
              type="button"
              key={`${trade.currencyPair}-${trade.executionTimestamp}`}
              className={classes.menuItem}
              role="menuitem"
              onClick={() => {
                onSelect(trade);
                setIsOpen(false);
              }}
            >
              <span className={classes.tradeLabel}>
                {`${trade.currencyPair.slice(0, 3)}/${trade.currencyPair.slice(3)}`}
                {" · "}
                {formatExecutionTimestampWithZone(trade.executionTimestamp, timeZone)}
                {` (${trade.rates})`}
              </span>
              <span className={classes.event}>{trade.event}</span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default BestXExampleTradeLoader;
